import React, { useState, useEffect } from 'react';
import { useAppConfig } from '../../../../context/AppConfigContext';

export const GeneralSettings: React.FC = () => {
  const { config: appConfig, updateConfig, isLoading } = useAppConfig();
  const [config, setConfig] = useState(appConfig);
  const [saving, setSaving] = useState(false);

  // Update local state when app config changes
  useEffect(() => {
    setConfig(appConfig);
  }, [appConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      updateConfig(config);
      await new Promise(resolve => setTimeout(resolve, 500));
      alert('General settings saved successfully! The changes will be reflected across the portal.');
    } catch (error) {
      alert('Failed to save general settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">General Settings</h2>
        <p className="text-gray-600">Configure basic portal settings and company information</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Name
          </label>
          <input
            type="text"
            value={config.companyName}
            onChange={(e) => setConfig({ ...config, companyName: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Portal Name
          </label>
          <input
            type="text"
            value={config.portalName}
            onChange={(e) => setConfig({ ...config, portalName: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Support Email
          </label>
          <input
            type="email"
            value={config.supportEmail}
            onChange={(e) => setConfig({ ...config, supportEmail: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <select
            value={config.timezone}
            onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
          >
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
            <option value="Europe/London">London</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <select
            value={config.language}
            onChange={(e) => setConfig({ ...config, language: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Format
          </label>
          <select
            value={config.dateFormat}
            onChange={(e) => setConfig({ ...config, dateFormat: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
      </div>

      {/* Homepage Customization Section */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Homepage Customization</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Logo URL
            </label>
            <input
              type="url"
              value={config.logoUrl || ''}
              onChange={(e) => setConfig({ ...config, logoUrl: e.target.value })}
              placeholder="https://example.com/logo.png"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
            />
            <p className="text-xs text-gray-500 mt-1">Enter the URL of your company logo (recommended size: 300x100px)</p>
            {config.logoUrl && (
              <div className="mt-3">
                <p className="text-sm text-gray-700 mb-2">Preview:</p>
                <img
                  src={config.logoUrl}
                  alt="Logo preview"
                  className="h-24 w-auto border border-gray-300 rounded p-2"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '';
                    (e.target as HTMLImageElement).alt = 'Failed to load image';
                  }}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Welcome Message
            </label>
            <textarea
              value={config.welcomeMessage || ''}
              onChange={(e) => setConfig({ ...config, welcomeMessage: e.target.value })}
              placeholder="Register as a customer or supplier to start your partnership with us"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
            />
            <p className="text-xs text-gray-500 mt-1">This message appears on the homepage below the welcome title</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-6 border-t">
        <button
          onClick={handleSave}
          disabled={saving || isLoading}
          className="px-8 py-3 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 font-medium"
        >
          {saving || isLoading ? 'Saving...' : 'Save General Settings'}
        </button>
      </div>
    </div>
  );
};
